/** Built-in workout templates (SPEC §7.5). Read-only; duplicate to edit. */
import type { Step, Target, Workout } from './model';
import { FTP20_LABEL } from './runner';

const ftp = (pct: number): Target => ({ type: 'ftp', pct });
const w = (watts: number): Target => ({ type: 'watts', w: watts });
const min = (m: number) => Math.round(m * 60);
const steady = (m: number, t: Target, label?: string, cadence?: number): Step => ({
  kind: 'steady',
  durationSec: min(m),
  target: t,
  ...(label ? { label } : {}),
  ...(cadence ? { cadence } : {}),
});
const ramp = (m: number, a: Target, b: Target, label?: string): Step => ({
  kind: 'ramp',
  durationSec: min(m),
  from: a,
  to: b,
  ...(label ? { label } : {}),
});
const repeat = (times: number, steps: Step[]): Step => ({ kind: 'repeat', times, steps });

/** Ramp test: 5 min @ 100 W, then 1-min steps from 100 W, +20 W each, until cadence < 50 rpm for 5 s. */
function rampTestSteps(): Step[] {
  const steps: Step[] = [steady(5, w(100), 'Aufwärmen')];
  for (let i = 0; i < 45; i++) steps.push(steady(1, w(100 + 20 * i), `Stufe ${i + 1}`));
  return steps;
}

const T0 = 1_700_000_000_000;

export const TEMPLATES: Workout[] = [
  {
    id: 'tpl-ramp-test',
    name: 'Rampentest',
    description:
      '5 Min. Aufwärmen mit 100 W, danach jede Minute +20 W ab 100 W bis zur Erschöpfung. Endet, wenn die Kadenz 5 s unter 50 rpm fällt. FTP = 0,75 × beste 1-Min.-Leistung.',
    tags: ['FTP-Test'],
    steps: rampTestSteps(),
    special: 'ramp-test',
    builtin: true,
    updatedAt: T0,
  },
  {
    id: 'tpl-ftp20',
    name: '20-Min.-FTP-Test',
    description:
      '20 Min. Aufwärmen mit 3 × 1 Min. hoher Kadenz, 5 Min. hart, 10 Min. locker, dann 20 Min. Vollgas im virtuellen Schalten (flach, du schaltest selbst), Ausfahren. FTP = 0,95 × 20-Min.-Schnitt.',
    tags: ['FTP-Test'],
    steps: [
      steady(5, ftp(55), 'Aufwärmen'),
      repeat(3, [steady(1, ftp(80), 'Hohe Kadenz', 110), steady(1, ftp(55), 'Locker')]),
      steady(9, ftp(65), 'Aufwärmen'),
      steady(5, ftp(105), 'Hart'),
      steady(10, ftp(50), 'Locker'),
      { kind: 'free', durationSec: min(20), label: FTP20_LABEL },
      ramp(10, ftp(60), ftp(40), 'Ausfahren'),
    ],
    special: 'ftp20',
    builtin: true,
    updatedAt: T0,
  },
  {
    id: 'tpl-endurance-60',
    name: "Grundlage 60'",
    description: 'Lockere Grundlagenausdauer in Zone 2.',
    tags: ['Grundlage'],
    steps: [
      ramp(10, ftp(45), ftp(65), 'Aufwärmen'),
      steady(45, ftp(68), 'Grundlage'),
      ramp(5, ftp(60), ftp(40), 'Ausfahren'),
    ],
    builtin: true,
    updatedAt: T0,
  },
  {
    id: 'tpl-sweetspot-3x12',
    name: "Sweet Spot 3×12'",
    description: '3 × 12 Min. bei 90 % FTP mit 4 Min. Pause.',
    tags: ['Sweet Spot'],
    steps: [
      ramp(10, ftp(45), ftp(70), 'Aufwärmen'),
      repeat(3, [steady(12, ftp(90), 'Sweet Spot'), steady(4, ftp(55), 'Erholung')]),
      ramp(6, ftp(60), ftp(40), 'Ausfahren'),
    ],
    builtin: true,
    updatedAt: T0,
  },
  {
    id: 'tpl-threshold-2x20',
    name: "Schwelle 2×20'",
    description: '2 × 20 Min. knapp unter der Schwelle.',
    tags: ['Schwelle'],
    steps: [
      ramp(12, ftp(45), ftp(75), 'Aufwärmen'),
      repeat(2, [steady(20, ftp(98), 'Schwelle'), steady(5, ftp(55), 'Erholung')]),
      ramp(8, ftp(60), ftp(40), 'Ausfahren'),
    ],
    builtin: true,
    updatedAt: T0,
  },
  {
    id: 'tpl-vo2-5x3',
    name: "VO2max 5×3'",
    description: '5 × 3 Min. bei 118 % FTP, 3 Min. Pause.',
    tags: ['VO2max'],
    steps: [
      ramp(12, ftp(45), ftp(75), 'Aufwärmen'),
      repeat(5, [steady(3, ftp(118), 'VO2max'), steady(3, ftp(50), 'Erholung')]),
      ramp(10, ftp(60), ftp(40), 'Ausfahren'),
    ],
    builtin: true,
    updatedAt: T0,
  },
  {
    id: 'tpl-recovery-30',
    name: "Erholung 30'",
    description: 'Sehr locker rollen.',
    tags: ['Erholung'],
    steps: [
      ramp(5, ftp(40), ftp(50), 'Einrollen'),
      steady(20, ftp(50), 'Locker'),
      ramp(5, ftp(50), ftp(40), 'Ausrollen'),
    ],
    builtin: true,
    updatedAt: T0,
  },
  {
    id: 'tpl-openers',
    name: 'Rennvorbereitung (Openers)',
    description: 'Kurze, knackige Reize am Vortag eines Rennens.',
    tags: ['Rennen'],
    steps: [
      ramp(15, ftp(45), ftp(70), 'Aufwärmen'),
      steady(5, ftp(75), 'Tempo'),
      repeat(3, [steady(1, ftp(105), 'Schwelle'), steady(2, ftp(55), 'Erholung')]),
      repeat(3, [steady(0.25, ftp(150), 'Sprint'), steady(1.75, ftp(50), 'Erholung')]),
      ramp(8, ftp(60), ftp(40), 'Ausfahren'),
    ],
    builtin: true,
    updatedAt: T0,
  },
];
